import ArticlesPage from '@bridebook/web/app-basic/components/articles/articles-page/articles-page';
import { Breadcrumbs } from '@bridebook/web/app-basic/components/breadcrumbs/breadcrumbs.component';
import Footer from '@bridebook/web/app-basic/components/footer/footer';
import Head from '@bridebook/web/app-basic/components/head/head';
import { TopBar } from '@bridebook/web/app-basic/components/header';
import { setAlternateLinks } from '@bridebook/web/app-basic/lib/app/app-slice';
import {
  saveArticlesList,
  setArticlesSupport,
  setRecordsFound,
} from '@bridebook/web/app-basic/lib/article/article-slice';
import { getArticlesList } from '@bridebook/web/app-basic/lib/article/selectors';
import { toArticleCard } from '@bridebook/web/app-shared/lib/articles/to-article-card';
import {
  EdgeCacheTTL,
  basicGSSPWrapper,
} from '@bridebook/web/app-basic/lib/get-basic-server-side-props-wrapper';
import { ArticlesInitialData } from '@bridebook/web/app-basic/lib/types';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getActiveArticleCategory } from '@bridebook/toolbox/src/articles/get-active-article-category';
import gazetteer from '@bridebook/toolbox/src/gazetteer';
import { countriesWithAdvice } from '@bridebook/toolbox/src/i18n/features';
import { getArticlesPromise } from '@bridebook/web/app-shared/lib/articles/get-articles';
import { getBreadcrumbsArticles } from '@bridebook/web/app-shared/lib/utils';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { AppType } from '@bridebook/web/pages/_app';
import { ARTICLES_PER_PAGE } from '@bridebook/web/lib/articles/constants/constants';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { getAlternateLinksForRegularPage } from '@bridebook/web/lib/utils/i18n-routing/get-alternate-links';
import { getArticlesMarketSupport } from '@bridebook/web/app-shared/lib/articles/get-articles-market-support';
import SignUpBanner from '@bridebook/web/app-basic/components/articles/sign-up-banner/sign-up-banner';
import { getIsMobile } from '@bridebook/web/app-basic/lib/app/selectors';
import { useArticleCategoryName } from '@bridebook/web/app-shared/lib/articles/hooks/use-article-category-name';

interface StaticProps {
  initialData: ArticlesInitialData;
}

const Advice = ({ initialData }: StaticProps) => {
  const isMobile = useSelector(getIsMobile);
  const market = useMarket();
  const router = useRouter();
  const { page } = router.query;
  const { t } = useTranslation('articles');
  const totalArticles = useSelector(getArticlesList).length;
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(page ? Number(page) : 1);
  const category = router.query.category?.[0];
  const activeCategory = typeof category === 'string' ? category : '';
  const articles = initialData.content.articles;
  const recordsFound = initialData.content.recordsFound;

  const articleCategory = getActiveArticleCategory(activeCategory, articles);
  const categoryName = useArticleCategoryName(activeCategory);

  const handleOnSelect = useCallback(
    (item: { selected: number }) => {
      const { selected } = item;

      setCurrentPage(selected + 1);
      // TODO: [i18n][bb-global] Create link wrapper
      router.push(
        `/${market.prefix}${UrlHelper.advice}${
          activeCategory.length === 0 ? '' : `/${activeCategory}`
        }?page=${selected + 1}`,
      );
    },
    [activeCategory, market.prefix, router],
  );

  useEffect(() => {
    if (articles && articles.length > 0) {
      dispatch(saveArticlesList(articles.map(toArticleCard)));
    }
  }, [articles, dispatch]);

  // Sync the records found with the store
  useEffect(() => {
    if (recordsFound !== totalArticles) {
      dispatch(setRecordsFound(recordsFound || 0));
    }
  }, [recordsFound, totalArticles, dispatch]);

  useEffect(
    () => () => {
      // When leaving the page, reset the records found
      dispatch(setRecordsFound(null));
    },
    [dispatch],
  );

  return (
    <>
      <Head
        title={articleCategory?.title || t('advice.pageTitle')}
        plainTitle={Boolean(articleCategory?.title)}
      />
      {isMobile && <SignUpBanner />}
      <TopBar />
      {!isMobile && (
        <Breadcrumbs
          breadcrumbsData={getBreadcrumbsArticles(categoryName || '', UrlHelper.advice)}
        />
      )}
      <ArticlesPage
        articles={articles}
        articlesLoaded={true}
        recordsFound={recordsFound}
        expectedCount={ARTICLES_PER_PAGE}
        articleCategory={articleCategory}
        handleOnSelect={handleOnSelect}
        activePage={currentPage}
        setCurrentPage={setCurrentPage}
      />
      <Footer />
    </>
  );
};

export const getServerSideProps = basicGSSPWrapper(
  ['articles'],
  async ({ context, initialAppState, store }) => {
    const { page, search } = context.query;
    const category = context.query.category?.[0];
    const { market, pathname } = initialAppState;

    const articlesSupport = await getArticlesMarketSupport(market.prefix);
    store.dispatch(setArticlesSupport(articlesSupport));

    // Return 404 if the market is not supported
    if (!articlesSupport) return { notFound: true };

    // Initial static query should match exactly the query used on client-side in useArticlesListQuery hook
    const articlesQuery: ArticlesInitialData['query'] = {
      category: market.prefix,
      searchString: typeof search === 'string' ? search : '',
      tag: typeof category === 'string' ? category : '',
      resultsPerPage: 7,
      pageNum: page ? Number(page) : 1,
    };

    // Set alternate links
    const supportedMarkets = countriesWithAdvice.map((country) =>
      gazetteer.getMarketByCountry(country),
    );
    const alternate = getAlternateLinksForRegularPage(pathname, supportedMarkets);
    store.dispatch(setAlternateLinks(alternate));

    const initialData: ArticlesInitialData = {
      query: articlesQuery,
      content: await getArticlesPromise(articlesQuery),
    };

    return {
      props: {
        initialData,
      },
    };
  },
  {
    vercelCacheControl: {
      maxAge: EdgeCacheTTL.WEEK,
      sMaxAge: EdgeCacheTTL.WEEK,
      staleWhileRevalidate: EdgeCacheTTL.MONTH,
    },
    cloudflareCacheControl: {
      maxAge: EdgeCacheTTL.DAY,
      sMaxAge: EdgeCacheTTL.DAY,
      staleWhileRevalidate: EdgeCacheTTL.WEEK,
    },
  },
);

Advice.AppType = AppType.Basic;

export default Advice;
